// Camera barcode scanning — the pure decision layer under CameraScanModal.
//
// TWO ENGINES, ONE CONTRACT. Where the browser has a native `BarcodeDetector` with real 1D support
// (Android Chrome — the platform a phone register actually runs on — plus ChromeOS/macOS) we use it:
// hardware-accelerated, a few ms per frame, driven once per camera frame. Everywhere else (Windows
// desktop Chrome, Safari, Firefox) we fall back to ZXing, loaded on demand so the main bundle never
// pays for it. Both feed the same handler with `{ text, format, box }`.
//
// ⚠️ WINDOWS CHROME LIES. It exposes the `BarcodeDetector` class but `getSupportedFormats()` comes
// back empty (or QR-only), so `"BarcodeDetector" in window` is NOT a usable feature test — construct
// the detector only when at least one 1D format is really supported, else report null so the caller
// falls back. This is the difference between "scans instantly on the till" and "silently never scans".
//
// Everything here is pure / DOM-free (except getNativeDetector's feature probe) so it is unit-testable.

/** Formats worth decoding at a retail counter, in BarcodeDetector naming. */
export const SCAN_FORMATS = [
  "upc_a",
  "upc_e",
  "ean_13",
  "ean_8",
  "code_128",
  "code_39",
  "code_93",
  "itf",
  "codabar",
  "qr_code",
  "data_matrix",
];

/**
 * The native detector, or null when the platform cannot actually decode 1D barcodes (see header).
 * QR-only support is treated as unusable — a register scans products, not posters.
 */
export async function getNativeDetector() {
  try {
    if (typeof window === "undefined" || !("BarcodeDetector" in window)) return null;
    const supported = await window.BarcodeDetector.getSupportedFormats();
    const formats = SCAN_FORMATS.filter((f) => supported.includes(f));
    if (!formats.some((f) => f !== "qr_code" && f !== "data_matrix")) return null;
    return new window.BarcodeDetector({ formats });
  } catch {
    return null;
  }
}

/**
 * The code forms to try against the catalog, most-likely first.
 *
 * A UPC-A **is** an EAN-13 with a leading zero, and camera decoders sit on the EAN side of that
 * identity while hardware wedge scanners (and therefore the codes saved in this catalog) sit on the
 * UPC side: ZXing reports "0036000291452", the keyboard scanner types "036000291452". Without the
 * alternate form a camera scan of a perfectly known product reads "No product found". Both directions
 * are covered because either form may be the one stored. Digits only — a Code 128 that happens to be
 * 12 long must not sprout a bogus sibling.
 */
export function normalizeScanCandidates(rawText, format) {
  const text = String(rawText == null ? "" : rawText).trim();
  if (!text) return [];
  const out = [text];
  const f = String(format || "").toLowerCase();
  if (/^\d+$/.test(text)) {
    if ((f === "ean_13" || f === "") && text.length === 13 && text.startsWith("0")) out.push(text.slice(1));
    if ((f === "upc_a" || f === "") && text.length === 12) out.push("0" + text);
  }
  return [...new Set(out)];
}

/**
 * Continuous-scan gate — the thing that makes a live camera behave like a hardware scanner instead
 * of a machine gun. The camera sees the same barcode 30+ times a second; exactly one of those may
 * become an add.
 *
 * Rules (each encodes a counter behavior, not a guess):
 *  - one code per `anyCodeMs` globally — two different barcodes visible in one frame (a shelf, a
 *    multipack) must not both ring up from a single aim. 400ms stays: shortening it re-opens the
 *    "lingered on a shelf, rang up the neighbour" hole, and it is not what made re-scans slow;
 *  - a code re-fires only after it has been OUT OF VIEW for `sameCodeMs`. The window SLIDES: every
 *    rejected sighting refreshes the timestamp, so holding an item steady never re-adds it — the
 *    cashier pulls it away and re-presents to add another, exactly the hardware-scanner motion.
 *    ⚠️ 900ms, down from 1500 (round-4 device report: "re-scan takes time, sometimes fast sometimes
 *    slow"). The sliding refresh means STRAY GLIMPSES during camera movement — a blurry frame that
 *    still decodes while panning away — keep restarting the clock, so the effective wait was
 *    1.5s + random glimpses. Steady-hold safety does NOT depend on the window's length (sliding
 *    handles that); the length only sets how long a deliberate re-present takes, and 0.9s matches
 *    the natural pull-away-and-return motion;
 *  - `touchAll(now)` re-arms every known code at once. Called when a blocking dialog (age verify,
 *    variant picker, Quick Add) closes: detection was paused during it, so the sliding window ran
 *    out while the cashier was busy, and the still-aimed barcode would instantly fire again.
 */
export function createScanGate({ sameCodeMs = 900, anyCodeMs = 400 } = {}) {
  const lastByCode = new Map();
  // -Infinity, not 0: "never seen" must always pass the window test. A 0 default reads as "seen at
  // epoch", which is indistinguishable from "just seen" under any small/mocked clock.
  let lastAny = -Infinity;
  return {
    accept(code, now = Date.now()) {
      const prev = lastByCode.has(code) ? lastByCode.get(code) : -Infinity;
      if (now - prev < sameCodeMs) {
        lastByCode.set(code, now); // sliding: still in view ⇒ keep suppressing
        return false;
      }
      if (now - lastAny < anyCodeMs) return false;
      lastByCode.set(code, now);
      lastAny = now;
      return true;
    },
    touchAll(now = Date.now()) {
      for (const k of lastByCode.keys()) lastByCode.set(k, now);
      lastAny = now;
    },
  };
}

/**
 * One detection per event. When a frame carries several barcodes the CENTER-most wins — the reticle
 * tells the cashier "the middle is what scans", so the code under it must be the one that rings up,
 * not whichever the decoder happened to list first. Detections without geometry (ZXing path) fall
 * back to first-listed, which is fine: ZXing reports one result per attempt anyway.
 */
export function pickPrimaryDetection(detections, viewWidth, viewHeight) {
  if (!Array.isArray(detections) || detections.length === 0) return null;
  if (detections.length === 1 || !viewWidth || !viewHeight) return detections[0];
  const cx = viewWidth / 2;
  const cy = viewHeight / 2;
  let best = detections[0];
  let bestD = Infinity;
  for (const d of detections) {
    const b = d && d.box;
    if (!b || !Number.isFinite(b.x)) return detections[0]; // any geometry missing ⇒ don't half-rank
    const dx = b.x + b.width / 2 - cx;
    const dy = b.y + b.height / 2 - cy;
    const dist = dx * dx + dy * dy;
    if (dist < bestD) {
      bestD = dist;
      best = d;
    }
  }
  return best;
}

/** Mean 0–255 luma below which the scene counts as dark enough to auto-fire the torch.
 *  ⚠️ Phone cameras run aggressive auto-gain: a room a human calls "bad light" gets brightened to a
 *  mean of 60–80 before we ever see the pixels, so the threshold must sit well above true black.
 *  70 was raised from 55 after a real-device report that dim light didn't fire the torch. */
export const AUTO_TORCH_DARK_LUMA = 70;

/**
 * Does this camera have a controllable torch? RETRIES, because the honest answer arrives late:
 * on Android Chrome `track.getCapabilities()` frequently omits `torch` for the first moments after
 * the stream starts and only reports it once the pipeline settles — a single read at t=0 concluded
 * "no torch" on a phone that has one, which hid the ☀ button AND disarmed the auto-torch (the exact
 * bug reported from a real device on 2026-08-17). Polls capabilities ~6× over ~2s and also asks
 * `ImageCapture.getPhotoCapabilities().fillLightMode`, which some Androids populate first.
 */
export async function probeTorchCapability(track, { tries = 6, intervalMs = 350, diag } = {}) {
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const d = diag || {};
  // Does this BROWSER BUILD even have a torch concept? False here is the definitive "impossible on
  // this browser" signal — no device quirk can route around a constraint the UA never implemented.
  try {
    if (typeof navigator !== "undefined" && navigator.mediaDevices && navigator.mediaDevices.getSupportedConstraints) {
      d.browserTorch = navigator.mediaDevices.getSupportedConstraints().torch === true ? "known" : "NOT-in-this-browser";
    }
  } catch {
    /* informational only */
  }
  for (let i = 0; i < tries; i++) {
    if (!track || track.readyState === "ended") {
      d.probe = "no-track";
      return false;
    }
    try {
      const caps = track.getCapabilities ? track.getCapabilities() : {};
      d.capsKeys = Object.keys(caps || {}).join(",") || "(empty)";
      if (caps && caps.torch) {
        d.probe = "caps";
        return true;
      }
    } catch (e) {
      d.capsErr = (e && e.name) || "err";
    }
    try {
      if (typeof window !== "undefined" && window.ImageCapture) {
        const pc = await new window.ImageCapture(track).getPhotoCapabilities();
        const modes = (pc && pc.fillLightMode) || [];
        d.fillLight = modes.join(",") || "(none)";
        if (modes.includes("flash") || modes.includes("torch")) {
          d.probe = "imagecapture";
          return true;
        }
      } else {
        d.fillLight = "no-ImageCapture";
      }
    } catch (e) {
      d.icErr = (e && e.name) || "err";
    }
    if (i < tries - 1) await wait(intervalMs);
  }
  // LAST RESORT — PROBE BY DOING. Some Androids never report torch through EITHER channel above yet
  // drive it fine (second real-device report, 2026-08-17: button still hidden while the phone
  // plainly has a flash). `advanced` constraint sets are allowed to be DISCARDED SILENTLY by spec, so
  // applyConstraints "succeeding" proves nothing — `getSettings().torch` is the ground truth that the
  // torch actually engaged. Costs at most a ~50ms blink at open on exactly the devices that lie.
  if (!track || track.readyState === "ended" || !track.applyConstraints || !track.getSettings) {
    d.probe = "no-apply-api";
    return false;
  }
  const lit = () => !!(track.getSettings() || {}).torch;
  // Shape 1 — the spec's advanced constraint set.
  try {
    await track.applyConstraints({ advanced: [{ torch: true }] });
    if (lit()) {
      await track.applyConstraints({ advanced: [{ torch: false }] }).catch(() => {});
      d.probe = "apply";
      return true;
    }
    d.probe = "apply-silently-discarded";
  } catch (e) {
    d.applyErr = (e && e.name) || "err";
    d.probe = "apply-threw";
  }
  // Shape 2 — torch as a BARE constraint. Some Android builds honor only this form; per spec an
  // unsupporting device throws OverconstrainedError (caught), and constraint-set replacement does
  // not disturb the running track's settings.
  try {
    await track.applyConstraints({ torch: true });
    if (lit()) {
      await track.applyConstraints({ torch: false }).catch(() => {});
      d.probe = "apply-basic";
      return true;
    }
  } catch (e) {
    d.applyBasicErr = (e && e.name) || "err";
  }
  return false;
}

/**
 * The decode crop for the ZXing fallback — THE fix for poor camera-scan hit rates (iOS report,
 * 2026-08-17: 1 of 4 barcodes read). Full-frame decode wastes the binarizer on walls and shelves;
 * cropping to the reticle area (center band, where the UI tells the cashier to hold the code) makes
 * the barcode dominate the image. That buys enough time to also enable TRY_HARDER, which multiplies
 * scan lines and tolerance for blur/tilt. Output is capped at `maxWidth` px wide so a decode attempt
 * stays fast on a phone CPU.
 *
 * Round-4 tuning (iOS Chrome "not so well"): capture is now requested at 1080p, the band is wider
 * and taller (86% × 1.9:1 — off-center holds still land inside), and the cap rose to 1280 so the
 * extra pixels actually REACH the decoder instead of being thrown away by the downscale. More
 * pixels per module is the single biggest lever against focus-hunt blur on iPhones, where focus
 * cannot be controlled from the web. Cost self-throttles: the loop schedules the next attempt only
 * after the current one finishes.
 */
export function cropRegion(videoWidth, videoHeight, { widthFrac = 0.86, aspect = 1.9, maxWidth = 1280 } = {}) {
  const vw = Math.max(1, videoWidth | 0);
  const vh = Math.max(1, videoHeight | 0);
  const sw = Math.max(1, Math.round(vw * widthFrac));
  const sh = Math.max(1, Math.min(vh, Math.round(sw / aspect)));
  const sx = Math.round((vw - sw) / 2);
  const sy = Math.round((vh - sh) / 2);
  const scale = Math.min(1, maxWidth / sw);
  return { sx, sy, sw, sh, dw: Math.max(1, Math.round(sw * scale)), dh: Math.max(1, Math.round(sh * scale)) };
}

/**
 * Auto-torch decision: EVERY sample must be dark, and there must be at least two of them.
 * One bright frame means the lighting is actually fine (a hand briefly over the lens, a shadow as
 * the phone is raised) — the torch must not strobe on for those. Samples are taken AFTER the
 * camera's auto-exposure settles, because every camera opens dark for its first few frames and a
 * single early reading would fire the torch in a perfectly lit store.
 */
export function shouldAutoTorch(lumaSamples, threshold = AUTO_TORCH_DARK_LUMA) {
  return (
    Array.isArray(lumaSamples) &&
    lumaSamples.length >= 2 &&
    lumaSamples.every((l) => Number.isFinite(l) && l < threshold)
  );
}

/**
 * Modest default zoom for the scanning camera, when the hardware supports it.
 *
 * WHY ZOOM AT ALL: phone cameras focus poorly at the close range a cashier instinctively uses. A
 * ~1.5× zoom means the barcode fills the frame from FURTHER AWAY — inside the lens's comfortable
 * focus range — which attacks the blur that no decoder setting can fix (focus is not controllable
 * from the web on iOS at all). Commercial web scanners default to a modest zoom for this reason.
 * Returns the zoom to apply, or null when the range is absent/too small to matter (< 1.3×) — a
 * clamped micro-zoom would change the view without helping focus.
 */
export function chooseZoom(zoomCaps, { target = 1.5 } = {}) {
  if (!zoomCaps || typeof zoomCaps.max !== "number") return null;
  const min = typeof zoomCaps.min === "number" ? zoomCaps.min : 1;
  if (zoomCaps.max < Math.max(1.3, min)) return null;
  const z = Math.min(target, zoomCaps.max);
  return z > min ? z : null;
}

/**
 * Which OTHER cameras are worth probing for a torch when the current track reports none.
 *
 * Multi-lens Androids expose several back cameras (main, wide, macro, depth…), and
 * `facingMode: ideal environment` lets Chrome bind ANY of them — on some devices it binds one whose
 * track never exposes `torch`, while a sibling back camera drives the same physical flash fine
 * (round-4 hypothesis for "flash icon still not visible on Android"). Candidates: video inputs other
 * than the current one, back/rear-labelled first, unlabelled ones after (labels exist post-
 * permission, but not always), FRONT cameras excluded outright — a selfie lens has no flash and
 * flashing the cashier's face would be the bug report after this one.
 */
export function pickTorchSiblingCandidates(devices, currentDeviceId, max = 4) {
  const vids = (devices || []).filter(
    (d) => d && d.kind === "videoinput" && d.deviceId && d.deviceId !== currentDeviceId
  );
  const backish = (d) => /back|rear|environment/i.test(d.label || "");
  const frontish = (d) => /front|user|face|selfie/i.test(d.label || "");
  return [...vids.filter(backish), ...vids.filter((d) => !backish(d) && !frontish(d))].slice(0, max);
}

/** ZXing numeric BarcodeFormat → BarcodeDetector-style lower-case name ("EAN_13" ⇒ "ean_13"). */
export function zxingFormatToName(value, BarcodeFormatEnum) {
  return String((BarcodeFormatEnum && BarcodeFormatEnum[value]) || "").toLowerCase();
}
